import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarCheck, ArrowLeft, Clock, Mail, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookedPage() {
  const [searchParams] = useSearchParams();
  const pkg = searchParams.get('package') || '';

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/track/booking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ package: pkg }),
    }).catch(() => {});
  }, [pkg]);

  const steps = [
    { icon: <Mail size={14} />, text: 'A calendar invite has been sent to the email you provided.' },
    { icon: <Clock size={14} />, text: 'Adam will personally reach out 24 hours before your scheduled call.' },
    { icon: <CheckCircle2 size={14} />, text: 'Come prepared — think about your current financial position and what you want to change.' },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16" style={{ backgroundColor: '#070A0F' }}>
      <div className="max-w-xl w-full">

        {/* Brand Mark */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-2.5 mb-14"
        >
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #D4AF37, #B8922E)' }}
          >
            <span className="text-black font-bold text-sm" style={{ fontFamily: 'Space Grotesk' }}>T</span>
          </div>
          <span className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk', color: '#F4F6FA' }}>
            TrepTradingTechnologies
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Icon */}
          <div className="mb-8">
            <div
              className="w-16 h-16 flex items-center justify-center"
              style={{ border: '2px solid #1E40AF', backgroundColor: 'rgba(30,64,175,0.07)' }}
            >
              <CalendarCheck size={30} style={{ color: '#2563EB' }} />
            </div>
          </div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            style={{
              fontFamily: 'Cormorant Garamond',
              fontSize: 'clamp(40px, 6vw, 60px)',
              fontWeight: 600,
              color: '#FFFFFF',
              lineHeight: '1.1',
              letterSpacing: '-0.5px',
              marginBottom: '12px',
            }}
          >
            Your call is booked.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="text-lg leading-relaxed mb-10"
            style={{ color: '#9CA3AF', fontFamily: 'DM Sans' }}
          >
            {pkg ? (
              <>
                <span style={{ color: '#93C5FD', fontWeight: 600 }}>{pkg}</span> package selected —{' '}
              </>
            ) : null}
            You made the right move. Now let it work for you.
          </motion.p>

          {/* What Happens Next */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mb-10 p-6 space-y-4"
            style={{ backgroundColor: '#0B1020', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <p
              className="text-xs font-medium mb-5"
              style={{ fontFamily: 'Azeret Mono', color: '#6B7B8E', letterSpacing: '0.09em', textTransform: 'uppercase' }}
            >
              What Happens Next
            </p>
            {steps.map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="mt-0.5 flex-shrink-0" style={{ color: '#2563EB' }}>{step.icon}</span>
                <span className="text-sm leading-relaxed" style={{ color: '#D1D5DB', fontFamily: 'DM Sans' }}>
                  {step.text}
                </span>
              </div>
            ))}
          </motion.div>

          {/* Quote */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="border-l-2 pl-4 mb-10"
            style={{ borderColor: '#1E40AF' }}
          >
            <p className="text-sm leading-relaxed italic" style={{ color: '#9CA3AF', fontFamily: 'DM Sans' }}>
              "Most people never take the first step. You did. See you on the call."
            </p>
            <p
              className="text-xs mt-2 font-medium"
              style={{ fontFamily: 'Azeret Mono', color: '#6B7B8E', letterSpacing: '0.06em' }}
            >
              — Adam, TrepTradingTechnologies
            </p>
          </motion.div>

          {/* Divider */}
          <div className="mb-8" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <a href="/">
              <Button
                data-testid="booked-return-home-button"
                className="h-11 px-6 text-sm font-medium inline-flex items-center gap-2"
                style={{
                  backgroundColor: 'transparent',
                  color: '#9CA3AF',
                  border: '1px solid rgba(255,255,255,0.12)',
                  fontFamily: 'DM Sans',
                  letterSpacing: '0.3px',
                }}
              >
                <ArrowLeft size={14} />
                Return Home
              </Button>
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-12 text-xs"
            style={{ color: '#374151', fontFamily: 'DM Sans' }}
          >
            TrepTradingTechnologies · Private &amp; Invitation Only
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
